import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useAdmin } from '../../context/AdminContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUtensils, faFileText, faCoins, faTag, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import AdminFormModal from '../shared/AdminFormModal';
import ModifierGroupsManager from './ModifierGroupsManager';

const AddItemModal = ({ show, onClose, onItemAdded, categories }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [kdvRate, setKdvRate] = useState('10');
  const [isAvailable, setIsAvailable] = useState(true);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [errors, setErrors] = useState({});
  const { areas } = useAdmin();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!name) nextErrors.name = 'Ürün adı gerekli.';
    if (!description) nextErrors.description = 'Açıklama gerekli.';
    if (!price) nextErrors.price = 'Fiyat gerekli.';
    if (!categoryId) nextErrors.categoryId = 'Kategori seçilmeli.';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const selectedCategory = categories.find((category) => category.id === parseInt(categoryId, 10));
    const menuId = selectedCategory ? selectedCategory.menuId : null;

    if (!menuId) {
      setErrors({ categoryId: 'Geçersiz kategori seçimi' });
      return;
    }

    onItemAdded({
      name,
      description,
      price: parseFloat(price),
      imageUrl: '',
      categoryId: parseInt(categoryId, 10),
      areaId: areaId ? parseInt(areaId, 10) : null,
      menuId,
      kdvRate: parseFloat(kdvRate),
      isAvailable,
      modifierGroups: modifierGroups.map((group, index) => ({
        id: group.id,
        sortOrder: index + 1,
      })),
    });

    setName('');
    setDescription('');
    setPrice('');
    setCategoryId('');
    setAreaId('');
    setKdvRate('10');
    setIsAvailable(true);
    setModifierGroups([]);
    setErrors({});
    onClose();
  };

  return (
    <AdminFormModal
      show={show}
      onClose={onClose}
      title="Yeni Ürün Ekle"
      subtitle="Temel ürün bilgisini, kategori yerleşimini ve alan kapsamını tek formda oluşturun."
      icon={faPlus}
      footer={(
        <>
          <div className="admin-modal-footer-secondary">
            <Button variant="secondary" onClick={onClose} className="modal-cancel-btn">
              İptal
            </Button>
          </div>
          <div className="admin-modal-footer-primary">
            <Button variant="primary" onClick={handleSubmit} className="modal-save-btn modal-save-btn-success">
              <FontAwesomeIcon icon={faPlus} />
              Ürün Ekle
            </Button>
          </div>
        </>
      )}
    >
      <Form onSubmit={handleSubmit} className="admin-form-section">
        <div className="admin-form-block">
          <div className="admin-form-block-title">
            <FontAwesomeIcon icon={faUtensils} />
            Temel Bilgiler
          </div>
          <div className="admin-form-grid">
            <div className="admin-modal-field">
              <Form.Label>Ürün Adı</Form.Label>
              <Form.Control type="text" value={name} onChange={(event) => setName(event.target.value)} className={`form-input ${errors.name ? 'error' : ''}`} required />
              {errors.name ? <div className="error-message">{errors.name}</div> : null}
            </div>
            <div className="admin-modal-field">
              <Form.Label>
                <FontAwesomeIcon icon={faCoins} className="form-icon" />
                Fiyat (₺)
              </Form.Label>
              <Form.Control type="number" step="0.01" min="0" value={price} onChange={(event) => setPrice(event.target.value)} className={`form-input ${errors.price ? 'error' : ''}`} required />
              {errors.price ? <div className="error-message">{errors.price}</div> : null}
            </div>
            <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
              <Form.Label>
                <FontAwesomeIcon icon={faFileText} className="form-icon" />
                Açıklama
              </Form.Label>
              <Form.Control as="textarea" rows={3} value={description} onChange={(event) => setDescription(event.target.value)} className={`form-input ${errors.description ? 'error' : ''}`} required />
              {errors.description ? <div className="error-message">{errors.description}</div> : null}
            </div>
          </div>
        </div>

        <div className="admin-form-block">
          <div className="admin-form-block-title">
            <FontAwesomeIcon icon={faTag} />
            Kapsam ve Vergi
          </div>
          <div className="admin-form-grid">
            <div className="admin-modal-field">
              <Form.Label>Kategori</Form.Label>
              <Form.Select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} className={`form-input ${errors.categoryId ? 'error' : ''}`} required>
                <option value="">Kategori seçiniz</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
              {errors.categoryId ? <div className="error-message">{errors.categoryId}</div> : null}
            </div>
            <div className="admin-modal-field">
              <Form.Label>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="form-icon" />
                Alan
              </Form.Label>
              <Form.Select value={areaId} onChange={(event) => setAreaId(event.target.value)} className="form-input">
                <option value="">Tüm alanlar</option>
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="admin-modal-field">
              <Form.Label>KDV Oranı (%)</Form.Label>
              <Form.Control type="number" step="1" min="0" max="100" value={kdvRate} onChange={(event) => setKdvRate(event.target.value)} className="form-input" />
            </div>
            <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
              <Form.Check
                type="switch"
                id="add-item-is-available"
                label={isAvailable ? 'Siparişe Açık' : 'Geçici Olarak Kapalı'}
                checked={isAvailable}
                onChange={(event) => setIsAvailable(event.target.checked)}
              />
            </div>
          </div>
        </div>

        <ModifierGroupsManager value={modifierGroups} onChange={setModifierGroups} modalVisible={show} />
      </Form>
    </AdminFormModal>
  );
};

export default AddItemModal;
